import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  usePlatformReinstateOrg,
  usePlatformSuspendOrg,
} from "@/lib/platform";

/**
 * Typed-confirm modal for suspend / reinstate. The admin must type the
 * target org name to enable the action — same UX as `DsrEraseModal`
 * (defence against muscle-memory clicks).
 */
export function SuspendOrgModal({
  orgId,
  orgName,
  mode,
  onClose,
}: {
  orgId: number | string;
  orgName: string;
  mode: "suspend" | "reinstate";
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const suspend = usePlatformSuspendOrg(orgId);
  const reinstate = usePlatformReinstateOrg(orgId);
  const mutation = mode === "suspend" ? suspend : reinstate;
  const ready = confirmName.trim() === orgName.trim() && orgName.length > 0;
  const label = mode === "suspend"
    ? t("ui.suspendOrgModal.suspend")
    : t("ui.suspendOrgModal.reinstate");

  return (
    <div
      className="modal-backdrop bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="suspend-modal-heading"
    >
      <form
        className="w-full max-w-md rounded-lg border border-nb-border bg-nb-surface p-6 shadow-xl"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!ready) return;
          await mutation.mutateAsync({ reason });
          onClose();
        }}
      >
        <h2 id="suspend-modal-heading" className="text-lg font-semibold">
          {t("ui.suspendOrgModal.heading", { label, orgName })}
        </h2>
        <p className="mt-2 text-sm text-nb-mute">
          {mode === "suspend"
            ? t("ui.suspendOrgModal.suspendDescription")
            : t("ui.suspendOrgModal.reinstateDescription")}
        </p>
        <label className="mt-4 block text-sm">
          <span className="text-nb-mute">{t("ui.suspendOrgModal.reasonLabel")}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
            data-testid="suspend-reason"
          />
        </label>
        <label className="mt-3 block text-sm">
          <span className="text-nb-mute">
            <Trans i18nKey="ui.suspendOrgModal.confirmPrompt" values={{ orgName }}>
              Type <span className="font-mono">{"{{orgName}}"}</span> to confirm
            </Trans>
          </span>
          <input
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            className="mt-1 w-full rounded-md border border-nb-border bg-nb-bg p-2 font-mono text-sm"
            data-testid="suspend-confirm-input"
          />
        </label>
        {mutation.isError && (
          <div className="mt-2 text-sm text-nb-danger" role="alert">
            {t("ui.suspendOrgModal.errorGeneric")}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t("ui.suspendOrgModal.cancel")}
          </button>
          <button
            type="submit"
            className={
              mode === "suspend" ? "btn-danger" : "btn-primary"
            }
            disabled={!ready || mutation.isPending}
            data-testid="suspend-submit"
          >
            {mutation.isPending ? t("ui.suspendOrgModal.saving") : label}
          </button>
        </div>
      </form>
    </div>
  );
}
